import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorFeature } from '@/types/core/connector';
import type { Page } from '@/types/shared/page';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

export type UnbindConnectorFeatureCredentialPayload = {
  featureId: UUID;
  slotName: string;
};

/**
 * Removes a single credential slot from a secretIds map, returning undefined
 * when no bindings remain.
 */
function withoutSlot(
  current: Readonly<Record<string, UUID>> | undefined,
  slot: string
): Record<string, UUID> | undefined {
  if (!current) return undefined;
  const next = { ...current };
  delete next[slot];
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Unbinds the secret bound to a single credential slot on a connector feature.
 * This is the explicit way to remove a binding — reconcile is additive and
 * never implicitly unbinds.
 */
export function useUnbindConnectorFeatureCredential(
  tenantId?: UUID,
  connectorId?: UUID
) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.unbindConnectorFeatureCredential(
      tenantId,
      connectorId
    ),
    mutationFn: async (payload: UnbindConnectorFeatureCredentialPayload) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');

      // Capture the current _rev before the mutation so we can poll for a newer one.
      const cachedFeature = qc.getQueryData<ConnectorFeature | null>(
        connectorQueryKeys.featureDetail(
          tenantId,
          connectorId,
          payload.featureId
        )
      );
      const prevRev = cachedFeature?._rev ?? -2;

      const ok = await connectorService.unbindConnectorFeatureCredential(
        tenantId,
        connectorId,
        payload.featureId,
        payload.slotName
      );
      if (!ok) throw new Error('Failed to unbind connector feature credential');

      // Poll until the read model reflects the change via a newer _rev.
      try {
        await pollUntilResourceNewerThan(
          () =>
            connectorService.findConnectorFeature(
              tenantId,
              connectorId,
              payload.featureId
            ),
          prevRev,
          { maxAttempts: 10, delayMs: 1000 }
        );
      } catch (error) {
        console.warn('Connector feature verification timed out:', error);
      }

      return payload;
    },
    onSuccess: (payload) => {
      qc.setQueriesData<InfiniteData<Page<ConnectorFeature>>>(
        {
          queryKey: connectorQueryKeys.featureListByConnector(
            tenantId,
            connectorId
          ),
        },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.map((item) =>
                item.featureId === payload.featureId
                  ? {
                      ...item,
                      secretIds: withoutSlot(item.secretIds, payload.slotName),
                    }
                  : item
              ),
            })),
          };
        }
      );

      qc.setQueryData<ConnectorFeature | null>(
        connectorQueryKeys.featureDetail(
          tenantId,
          connectorId,
          payload.featureId
        ),
        (current) =>
          current
            ? {
                ...current,
                secretIds: withoutSlot(current.secretIds, payload.slotName),
              }
            : current
      );

      qc.invalidateQueries({
        queryKey: connectorQueryKeys.featureDetail(
          tenantId,
          connectorId,
          payload.featureId
        ),
      });
    },
  });
}
